<template>
	<div class="app flex-row align-items-center">
		<div class="container">
			<loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

			<b-row class="justify-content-center">
				<b-col md="8">
					<b-card-group>
						<b-card no-body class="p-4">
							<b-card-body>
								<div class="d-sm-none img-logo-small">
									<img src="img/logo.svg" class="ayun-logo-small" alt="Ayun! Logo" />
								</div>

								<b-form @submit.prevent="login" novalidate v-show="!has2WayAuth">
									<h3 class="title">Login</h3>
									<p class="text-muted">
										Enter your credentials here
									</p>

									<b-input-group class="mb-4 input-with-validation">
										<b-input-group-prepend>
											<b-input-group-text class="box">
												<span class="icon">
													<i class="icon-envelope"></i>
												</span>
											</b-input-group-text>
										</b-input-group-prepend>
										<input type="email" name="Email" v-model="email" v-validate="'required|email'"
											class="form-control" placeholder="Email" autocomplete="username email" />
									</b-input-group>
									<span v-if="errors.has('Email')" class="help-block">{{
										errors.first('Email')
									}}</span>

									<b-input-group class="mb-4 input-with-validation">
										<b-input-group-prepend>
											<b-input-group-text class="box">
												<span class="icon">
													<i class="icon-lock"></i>
												</span>
											</b-input-group-text>
										</b-input-group-prepend>
										<input type="password" name="Password" v-model="password" v-validate="{
											required: true,
											regex: passwordRegex,
										}" class="form-control" placeholder="Password" autocomplete="off" />
									</b-input-group>
									<span v-if="errors.has('Password')" class="help-block">{{ passwordErrorMsg }} &nbsp;
									</span>

									<b-row class="input-with-validation">
										<b-col sm="12">
											<b-button hidden id="recaptcha-login" type="button" variant="primary"
												class="px-4" :disabled="disableConfirmButtons" ref="recaptchaLogin" />

											<b-button id="btn-login" class="login" @click="validateBeforeLogin"
												:disabled="disableConfirmButtons">
												Login
											</b-button>
										</b-col>
									</b-row>

									<b-row>
										<b-col sm="12">
											<b-button variant="link" class="link" v-b-modal.forgot-password
												:disabled="disableConfirmButtons">
												Forgot password?
											</b-button>
											<br />
											<b-link to="/privacy-policy" target="_blank" class="link">
												Privacy Policy
											</b-link>
											<p class="deployment">
												{{ deployment }}
											</p>
										</b-col>
									</b-row>
								</b-form>

								<b-form @submit.prevent="submitCode" novalidate v-show="has2WayAuth">
									<h1>Verify</h1>
									<p class="text-muted">
										The verification code was sent to your
										phone number (
										{{ phoneNumberHint }}) via text message.
									</p>

									<b-input-group class="mt-3 mb-4 input-with-validation">
										<b-input-group-prepend>
											<b-input-group-text>
												<i class="icon-key"></i>
											</b-input-group-text>
										</b-input-group-prepend>
										<input type="number" name="Code" v-model="code" v-validate="{
											required: has2WayAuth,
											regex: '^[0-9]{6}$',
										}" maxlength="6" class="form-control" placeholder="Code" />
									</b-input-group>
									<span v-if="errors.has('Code')" class="help-block">{{
										'Enter 6-digit code sent to your phone.'
									}}</span>

									<b-row class="mt-3 input-with-validation">
										<b-col sm="12">
											<b-button type="submit" variant="primary" :disabled="
												disableConfirmButtons
											">
												Submit
											</b-button>
											<br />
											<b-button variant="link" class="px-0" id="recaptcha-resend-code"
												@click="sendSMSCode" :disabled="
													disableConfirmButtons ||
													isCounterRunning
												">
												{{ resendCodeDisplay }}
											</b-button>
											<br />
											<b-link @click="resetLogin">
												Reset Login
											</b-link>
										</b-col>
									</b-row>
								</b-form>
							</b-card-body>
						</b-card>
						<b-card no-body class="text-white bg-primary py-5 d-md-down-none logo-background">
							<b-card-body class="text-center">
								<div>
									<img src="ayun-logo-with-text.svg" class="img-logo" alt="Ayun! Logo" />
								</div>
								<p class="text-muted">
									You move it, we track it.
								</p>
							</b-card-body>
						</b-card>
					</b-card-group>
				</b-col>
			</b-row>
		</div>

		<forgot-password />
		<expired-password />
		<ForceLogout :email="email" />
	</div>
</template>

<script>
// Component
import ForceLogout from '@/views/account/ForceLogout';
import ForgotPassword from '@/views/account/ForgotPassword';
import ExpiredPassword from '@/views/account/ExpiredPassword';

// Util
import { ValidationUtil } from '@/utils/validationUtil';
import { UserUtil } from '@/utils/userutil';

// API
import userSessionApi from '@/api/userSessionApi';
import authApi from '@/api/authApi';
import userApi from '@/api/userApi';

// Others
import { firebase, remoteConfig } from '@/config/firebase';
import config from '@/config/env-constants';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import _ from 'lodash';

export default {
	name: 'Login',
	components: {
		ForgotPassword,
		Loading,
		ForceLogout,
		ExpiredPassword,
	},
	data() {
		return {
			email: '',
			password: '',
			code: '',
			deployment: '',

			// Check for loader
			isLoading: false,
			currUser: {},
			currCompany: {},

			has2WayAuth: false,
			resolver: {},

			recaptchaVerifier: {},
			recaptchaVerifierResendCode: {},
			recaptchaResponse: {},
			verificationId: '',
			phoneNumberHint: '',

			countDown: 120,
			isCodeExpired: false,
			isCounterRunning: false,
			resendCodeDisplay: 'Resend Code?',
			phoneAuthProvider: new firebase.auth.PhoneAuthProvider(),
		};
	},
	computed: {
		disableConfirmButtons() {
			return this.isLoading;
		},
		passwordRegex() {
			return config.passwordRegex;
		},
		passwordErrorMsg() {
			return config.passwordErrorMsg;
		},
	},
	async mounted() {
		this.deployment = await this.getDeployment();

		// Reset values
		this.has2WayAuth = false;
		this.verificationId = '';
		this.phoneNumberHint = '';

		this.loadLoginRecaptcha();
	},
	watch: {
		countDown(_value) {
			this.updateResendCodeDisplay();
		},
	},
	methods: {
		loadLoginRecaptcha() {
			let vm = this;
			if (_.isEmpty(this.recaptchaVerifier)) {
				this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
					'recaptcha-login',
					{
						size: 'invisible',
						callback: (_response) => {
							vm.onSolvedRecaptcha('recaptcha-login');
						},
					}
				);

				// Pre-render the reCAPTCHA
				this.recaptchaVerifier.render().then((widgetId) => {
					window.recaptchaWidgetId = widgetId;
				});
			}
		},
		loadResendCodeRecaptcha() {
			let vm = this;
			if (_.isEmpty(this.recaptchaVerifierResendCode)) {
				this.recaptchaVerifierResendCode =
					new firebase.auth.RecaptchaVerifier(
						'recaptcha-resend-code',
						{
							size: 'invisible',
							callback: (_response) => {
								vm.onSolvedRecaptcha('recaptcha-resend-code');
							},
						}
					);

				// Pre-render the reCAPTCHA
				this.recaptchaVerifierResendCode.render().then((widgetId) => {
					window.recaptchaWidgetId = widgetId;
				});
			}
		},
		updateResendCodeDisplay() {
			this.resendCodeDisplay = 'Resend Code?';
			if (this.isCounterRunning) {
				this.resendCodeDisplay =
					'Code will expire at ' + this.countDown + ' (s)';
			} else if (this.isCodeExpired) {
				this.resendCodeDisplay = 'Code expired. Resend Code?';
			}
		},
		onSolvedRecaptcha(fromScreen) {
			if (fromScreen === 'recaptcha-resend-code' && this.has2WayAuth) {
				// resend sms code
				this.sendSMSCode(this.recaptchaVerifierResendCode);
			} else {
				// login
				this.login();
			}
		},

		processFormValues() {
			// Removes excess whitespace
			this.email = ValidationUtil.removeExcessWhiteSpace(this.email);
		},
		async validateBeforeLogin() {
			this.processFormValues();

			let isValid = await this.$validator.validateAll();
			if (!isValid) {
				return;
			}

			const { data } = await userApi.validateLogin(
				this.email,
				this.password
			);

			if (!data.isSuccess) {
				isValid = data.isSuccess;
				this.$toaster.warning(data.message);

				// Reset password when the account is locked
				if (data.isAccountLocked) {
					this.resetPassword();
				} else if (data.isPasswordExpired) {
					this.$bvModal.show('expired-password');
				}
				return;
			}

			this.currUser = data.user;
			this.currCompany = data.company;

			this.$refs.recaptchaLogin.click();
		},
		async resetPassword() {
			try {
				await firebase.auth().sendPasswordResetEmail(this.email);
			} catch (error) {
				this.$toaster.error(
					'Error sending reset password email. Please try again.'
				);
			}
		},

		resetLogin(resetFields) {
			// reset form fields
			if (resetFields) {
				this.email = '';
				this.password = '';
			}
			this.code = '';
			this.has2WayAuth = false;
			this.isLoading = false;

			// reset validation
			this.$validator.reset();
			this.errors.clear();
			this.recaptchaVerifier.reset();
		},

		async login() {
			try {
				this.isLoading = true;

				await firebase
					.auth()
					.signInWithEmailAndPassword(this.email, this.password);

				await this.redirectToDashboard(this.currUser, this.currCompany);
			} catch (error) {
				if (error.code == 'auth/wrong-password') {
					// if error code is wrong password, increment login attempt
					await this.setAttempts(error.code);
					this.$toaster.warning(error.message);
					this.resetLogin(false);
					return;
				} else if (error.code == 'auth/multi-factor-auth-required') {
					// The user is a multi-factor user. Second factor challenge is required.
					this.resolver = error.resolver;
					this.sendSMSCode(this.recaptchaVerifier);
					return;
				} else {
					this.$toaster.warning(error.message);
					this.resetLogin(false);
					return;
				}
			}
		},
		async setAttempts(_errCode) {
			let userAttempts = {
				emailAddress: this.email,
				deviceType: 'web',
			};
			//call api to set attempts
			await userSessionApi.setUserSessionAttempts(userAttempts);
		},

		sendSMSCode(recaptchaVerifier) {
			let multiFactorHint = this.resolver.hints[0];

			// for display
			this.phoneNumberHint = multiFactorHint.phoneNumber;

			let phoneInfoOptions = {
				multiFactorHint: multiFactorHint,
				session: this.resolver.session,
			};

			// Send SMS verification code
			return this.phoneAuthProvider
				.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
				.then((verificationId) => {
					this.verificationId = verificationId;

					this.isLoading = false;
					this.$toaster.success(
						`6-digit code was sent to your phone number ${this.phoneNumberHint}`
					);

					this.countDown = 120;
					this.isCounterRunning = true;
					this.isCodeExpired = false;
					this.has2WayAuth = true;

					this.startCounter();
					this.loadResendCodeRecaptcha();

					return verificationId;
				})
				.catch((error) => {
					this.$toaster.warning(
						'Error verifying code. Please try again.'
					);

					this.isLoading = false;

					this.countDown = 0;
					this.isCounterRunning = false;
					this.isCodeExpired = false;
					this.has2WayAuth = false;

					return error;
				});
		},
		startCounter() {
			if (this.countDown > 0) {
				setTimeout(() => {
					this.countDown -= 1;
					this.startCounter();
				}, 1000);
			} else {
				this.isCounterRunning = false;
				this.isCodeExpired = true;
			}
		},

		async submitCode() {
			if (this.isCodeExpired) {
				this.$toaster.warning(`Please resend new code to verify.`);
				return;
			}

			this.isLoading = true;

			if (this.code.length !== 6) {
				this.$toaster.warning(
					'Invalid code format. Please input the 6-digit code sent to your phone.'
				);
				this.isLoading = false;
				return;
			}

			try {
				// Ask user for the SMS verification code.
				let cred = firebase.auth.PhoneAuthProvider.credential(
					this.verificationId,
					this.code
				);
				let multiFactorAssertion =
					firebase.auth.PhoneMultiFactorGenerator.assertion(cred);

				await this.resolver.resolveSignIn(multiFactorAssertion);
				await this.redirectToDashboard(this.currUser, this.currCompany);
			} catch (error) {
				this.isLoading = false;
				this.recaptchaVerifierResendCode.reset();
				this.$toaster.error('Error submitting code. Please try again.');
			}
		},

		async redirectToDashboard(currUser, currCompany) {
			// initialize the token after successful login
			await this.$store.dispatch('authenticateUser', {
				email: this.email,
				password: this.password,
			});

			// log the user details to session
			await this.$store.dispatch('logUserCredentials', {
				currUser: currUser,
				currCompany: currCompany,
			});

			// redirect to proper dashboard screen
			if (UserUtil.isSuperAdmin(currUser.emailAddress)) {
				await this.getInitUserData(currUser.id, config.view.ADMIN);
				this.showValidationMsg('Login successful.', true);
				this.$router.push('/admin/dashboard');
			} else {
				await this.getInitUserData(currUser.id, config.view.COMPANY);
				this.showValidationMsg('Login successful.', true);
				this.$router.push('/dashboard');
			}
		},
		async getInitUserData(userId, view) {
			let { data } = await authApi.getUserInitData(userId, view);

			this.$store.dispatch('setAllCompanies', data.companies);
			this.$store.dispatch('setAllStorageLocations', data.storageLocations);
			this.$store.dispatch('setAllConnections', data.connections);
			this.$store.dispatch('setAllUsers', data.users);
			this.$store.dispatch('setAllDriverUsers', data.driverUsers);
			this.$store.dispatch('setAllAssetTypeCategories', data.assetTypeCategories);
			this.$store.dispatch('setAllUoms', data.uoms);
			this.$store.dispatch('setAllIncidentTypes', data.incidentTypes);
			this.$store.dispatch('setAllAssetTypes', data.assetTypes);
			this.$store.dispatch('setAllTransportations', data.transportations);

			if (view === config.view.COMPANY) {
				this.$store.dispatch(
					'setAllConnectedCompanies',
					data.connectedCompanies
				);
				this.$store.dispatch(
					'setAllConnectedStorageLocations',
					data.connectedStorageLocations
				);
				this.$store.dispatch(
					'setAllConnectedUsers',
					data.connectedUsers
				);
			}
		},
		showValidationMsg(message, isValid) {
			if (isValid) {
				this.$toaster.success(message);
			} else {
				this.$toaster.warning(message);
			}

			// Hide loader
			this.isLoading = false;
		},
		async getDeployment() {
			await remoteConfig.fetchAndActivate();
			return remoteConfig.getString('deployment');
		},
	},
};
</script>

<style scoped>
.title {
	color: #122b91;
	font-weight: bold;
}

.login {
	background-color: #122b91;
	color: white;
}

.img-logo {
	margin-top: 50px;
	width: 80%;
	height: 80%;
}

.img-logo-small {
	width: inherit;
	text-align: center;
}

.ayun-logo-small {
	width: 55%;
	height: 55%;
}

.logo-background {
	background-color: #122b91 !important;
	border-color: #122b91 !important;
}

.deployment {
	margin-top: 8px;
	color: #23282c;
}

.link {
	padding-left: 0px;
	color: #f18f01;
}

.icon {
	color: white;
}

.form-control {
	border-color: #f18f01;
}

.box {
	background-color: #f18f01;
	border-color: #f18f01;
}
</style>
